import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import * as DynamicActions from './dynamic.actions';
import * as DynamicSelectors from './dynamic.selectors';
import { DynamicState } from './dynamic.state';
import { UiComponent } from '../models/ui-component.models';
import { distinctUntilChanged, Observable, last, first } from 'rxjs';
import { UiAction } from '../models/ui-action.models';
import { UiDefinition } from '../models/ui-definition.models';
import { isEqual } from 'lodash-es';

@Injectable()
export class DynamicFacade {
    constructor(
        private dynamicStore: Store<DynamicState>
    ) {
    }

    public actions$ = this.dynamicStore.select(DynamicSelectors.getActions);
    public uiDefinition$ = this.dynamicStore.select(DynamicSelectors.uiDefinition);
    public presentation$ = this.dynamicStore.select(DynamicSelectors.presentation);

    public data$(storeName: string, storeDataName: string, storeDataPath: string, storeDataChildNames: string[]): Observable<string> {
        return this.dynamicStore.select(DynamicSelectors.getStoreData(storeName, storeDataName, storeDataPath, storeDataChildNames)).pipe(
            distinctUntilChanged(isEqual)
        );
    }

    public dataAlways$(storeName: string, storeDataName: string, storeDataPath: string, storeDataChildNames: string[]): Observable<string> {
        return this.dynamicStore.select(DynamicSelectors.getStoreData(storeName, storeDataName, storeDataPath, storeDataChildNames));
    }

    InitLocalStore(uiComponents: UiComponent[]) {
        this.dynamicStore.dispatch(DynamicActions.initLocalStore({ uiComponents: uiComponents, storeDataChildNames: [] }));
    }

    InitChildStore(uiComponents: UiComponent[], storeDataChildNames: string[], name: string) {
        this.dynamicStore.dispatch(DynamicActions.initChildStore({ uiComponents: uiComponents, storeDataChildNames: [...storeDataChildNames, name], name: name }));
    }

    RemoveActionFromStore(storeDataChildNames: string[], name: string) {
        this.dynamicStore.dispatch(DynamicActions.removeActionFromStore({ storeDataChildNames: storeDataChildNames, name: name }));
    }

    GetAction(actionName: string, storeDataChildNames: string[]): Observable<UiAction> {
        return this.dynamicStore.select(DynamicSelectors.getAction(actionName, storeDataChildNames));
    }

    ExecuteAction(actionName: string, storeDataChildNames: string[], actionPath: String = "") {
        if (actionName) {
            this.dynamicStore.dispatch(DynamicActions.executeAction({ actionName: actionName, actionPath: actionPath, storeDataChildNames: storeDataChildNames }));
        }
    }

    ExecuteStoredProcedure(uiAction: UiAction, storeDataChildNames: string[], actionPath: String = "") {
        this.dynamicStore.dispatch(DynamicActions.executeStoredProcedure({ uiAction: uiAction, storeDataChildNames: storeDataChildNames, actionPath: actionPath }));
    }

    GeneratePDF(uiAction: UiAction, storeDataChildNames: string[], actionPath: String = "") {
        this.dynamicStore.dispatch(DynamicActions.generatePDF({ uiAction: uiAction, storeDataChildNames: storeDataChildNames, actionPath: actionPath }));
    }

    SendMail(uiAction: UiAction, storeDataChildNames: string[], actionPath: String = "") {
        this.dynamicStore.dispatch(DynamicActions.sendMail({ uiAction: uiAction, storeDataChildNames: storeDataChildNames, actionPath: actionPath }));
    }

    ExportCsv(uiAction: UiAction, storeDataChildNames: string[], actionPath: String = "") {
        this.dynamicStore.dispatch(DynamicActions.exportCsv({ uiAction: uiAction, storeDataChildNames: storeDataChildNames, actionPath: actionPath }));
    }

    GetData(storeName: string, storeDataName: string, storeDataPath: string, storeDataChildNames: string[]): Observable<string> {
        return this.dynamicStore.select(DynamicSelectors.getStoreData(storeName, storeDataName, storeDataPath, storeDataChildNames));
    }

    SetData(storeName: string, storeDataName: string, storeDataPath: string, storeDataChildNames: string[], newValue: string, actionName: string) {
        switch (storeName.toLowerCase()) {
            case 'local':
                this.dynamicStore.dispatch(DynamicActions.saveDataInLocalStore({ storeDataName: storeDataName, storeDataPath: storeDataPath, storeDataChildNames: storeDataChildNames, newValue: newValue, actionName: actionName }));
                break;
            case 'session':
                this.dynamicStore.dispatch(DynamicActions.saveDataInSessionStore({ storeDataName: storeDataName, storeDataPath: storeDataPath, newValue: newValue, actionName: actionName }));
                break;
            default:
                console.warn(`Unknown store name: ${storeName}`);
        }
    }

    RemoveData(storeName: string, storeDataName: string, storeDataPath: string, storeDataChildNames: string[], actionName: string) {
        this.dynamicStore.dispatch(DynamicActions.removeDataInStore({ storeName: storeName, storeDataName: storeDataName, storeDataPath: storeDataPath, storeDataChildNames: storeDataChildNames, actionName: actionName }))
    }

    GetUiDefinitionFromUrl(url: string) {
        this.dynamicStore.dispatch(DynamicActions.getUiDefinitionFromUrl({ url: url, currentPersonId: "", currentVereinId: "", currentAnlassId: "" }));
    }

    GetUiDefinitionFromName(name: string, currentPersonId: string = "", currentVereinId: string = "", currentAnlassId: string = "") {
        this.dynamicStore.dispatch(DynamicActions.getUiDefinitionFromName({ name: name, currentPersonId: currentPersonId, currentVereinId: currentVereinId, currentAnlassId: currentAnlassId }));
    }

    GetPresentation(id: string) {
        this.dynamicStore.dispatch(DynamicActions.getPresentation({ id: id }));
    }

    SetDataInArrayWithId(storeName: string, storeDataName: string, storeDataPath: string, idKey: string, idKeyValue: any, changeKey: string, changeValue: any, storeDataChildNames: string[], actionName: string) {
        this.dynamicStore.dispatch(DynamicActions.saveDataInArrayWithIdInStore({
            storeName: storeName,
            storeDataName: storeDataName,
            storeDataPath: storeDataPath,
            idKey: idKey,
            idKeyValue: idKeyValue,
            changeKey: changeKey,
            changeValue: changeValue,
            storeDataChildNames: storeDataChildNames,
            actionName: actionName
        }));
    }

}
